/**
 * Beacon — the visible help affordance that lives next to every guided
 * UI zone.
 *
 * iter358.1 (Mar 4 2026) — PROMINENT REDESIGN.
 *   Customer feedback: "I don't see such symbol anywhere" + "round info
 *   icon is not prominent". The amber pulse was too subtle. Re-designed
 *   so customers can actually find it:
 *     • Larger by default (24-32px responsive).
 *     • Brighter gradient (warm amber → rosegold).
 *     • Continuous double-ring pulse so the eye lands on it even on
 *       busy backgrounds.
 *     • First-visit "What's this?" hint label that auto-fades after a
 *       few seconds — makes the affordance discoverable without nag.
 *     • Bold "?" glyph (not the thin lucide HelpCircle).
 *
 *   Placement still uses notification-badge model from iter357 so the
 *   beacon never overlaps text/buttons.
 *
 * Anchor variants (anchor="<mode>"):
 *   • "badge"   — DEFAULT. Floats at the top-right corner OUTSIDE the
 *                 element border (negative offset).
 *   • "header"  — Inline, no absolute positioning.
 *   • "inset"   — Inside the element (use only with padding to spare).
 *
 * Placement contract:
 *   • Parent of <Beacon anchor="badge" /> must be `position: relative`.
 *
 * A11y:
 *   • Focusable, keyboard-activatable, aria-label.
 *   • Respects prefers-reduced-motion.
 */
import { useEffect, useRef, useState } from "react";

import useTour from "./useTour";

const ANCHOR_CLASSES = {
    // "badge" — corner notification style. Negative offset so the
    // beacon sits OUTSIDE the element border, never covering content.
    "badge":          "absolute -top-3 -right-3",
    "badge-tl":       "absolute -top-3 -left-3",
    "badge-bl":       "absolute -bottom-3 -left-3",
    "badge-br":       "absolute -bottom-3 -right-3",
    // "inset" — positive offset, inside the element.
    "inset":          "absolute top-2 right-2",
    "inset-tl":       "absolute top-2 left-2",
    // "header" — inline, plays well in flex header rows.
    "header":         "static",
    "inline":         "static ml-1",
    // Back-compat aliases.
    "top-right":      "absolute -top-3 -right-3",
    "top-left":       "absolute -top-3 -left-3",
    "bottom-right":   "absolute -bottom-3 -right-3",
    "bottom-left":    "absolute -bottom-3 -left-3",
};

const HINT_FADE_AFTER_MS = 4500;  // Hint shows for 4.5s on first land.

export default function Beacon({
    id,
    anchor = "badge",
    size = "md",
    tone = "amber",
    label = null,
    showHint = true,        // Show "What's this?" hint on first-visit.
    hintText = "What's this?",
    testid = null,
}) {
    const tour = useTour();
    const isOpen = tour.openBeaconId === id;
    const isSeen = tour.hasBeenSeen(id);
    const [hintVisible, setHintVisible] = useState(false);
    const hintFiredRef = useRef(false);

    // Show the "What's this?" label exactly once per beacon-instance,
    // when it first mounts AND the customer hasn't engaged with this
    // beacon before. Auto-fades after HINT_FADE_AFTER_MS.
    useEffect(() => {
        if (hintFiredRef.current) return undefined;
        if (!showHint) return undefined;
        if (isSeen) return undefined;
        if (isOpen) return undefined;
        if (tour.isAutoWalk) return undefined;  // Don't compete with walk bubble.
        hintFiredRef.current = true;
        // Slight stagger so multiple beacons on a page don't all bloom
        // at the exact same millisecond.
        const stagger = Math.random() * 600;
        const showT = setTimeout(() => setHintVisible(true), 900 + stagger);
        const hideT = setTimeout(() => setHintVisible(false), 900 + stagger + HINT_FADE_AFTER_MS);
        return () => { clearTimeout(showT); clearTimeout(hideT); };
    }, [showHint, isSeen, isOpen, tour.isAutoWalk]);

    function handleClick(e) {
        e.preventDefault();
        e.stopPropagation();
        setHintVisible(false);
        if (isOpen) tour.closeBeacon();
        else tour.openBeacon(id);
    }

    // iter (Jun 2026) — keyboard activation for the role="button" span.
    function handleKeyDown(e) {
        if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") {
            handleClick(e);
        }
    }

    const sizeClass =
        size === "sm" ? "jns-tour-beacon-size-sm" :
        size === "lg" ? "jns-tour-beacon-size-lg" :
        "jns-tour-beacon-size-md";
    const positionClass = ANCHOR_CLASSES[anchor] || ANCHOR_CLASSES.badge;
    const isFloating = anchor !== "static" && anchor !== "header" && anchor !== "inline";

    return (
        <span className={isFloating ? `${positionClass} z-30` : "inline-flex items-center"} style={{ pointerEvents: "auto" }}>
            {/* role="button" span (not a real <button>): beacons are often
                placed inside other buttons (e.g. a step's submit CTA), and a
                nested <button> is invalid HTML — it spammed hydration errors
                and made clicks unreliable. A span is valid phrasing content
                inside a button; stopPropagation keeps the parent from firing. */}
            <span
                role="button"
                tabIndex={0}
                onClick={handleClick}
                onKeyDown={handleKeyDown}
                aria-label={label || "What is this?"}
                title={label || "What is this?"}
                data-tour-target={id}
                data-testid={testid || `tour-beacon-${id}`}
                data-tour-tone={tone}
                data-tour-open={isOpen ? "true" : "false"}
                data-tour-unseen={!isSeen && !isOpen ? "true" : "false"}
                className={`${sizeClass} relative inline-flex items-center justify-center rounded-full cursor-pointer transition-all duration-300 jns-tour-beacon ${
                    isOpen
                        ? "jns-tour-beacon-active"
                        : isSeen
                            ? "jns-tour-beacon-idle"
                            : "jns-tour-beacon-unseen"
                }`}
            >
                {/* Big bold "?" or "✕" — text glyph reads more clearly
                    than a thin icon at small sizes. */}
                <span className="jns-tour-beacon-glyph select-none">
                    {isOpen ? "×" : "?"}
                </span>
                {/* Continuous double-ring halo (always visible — separate
                    from the pulse). Makes the beacon obvious on any
                    background. Hidden when the bubble is open. */}
                {!isOpen && <span className="jns-tour-beacon-halo" aria-hidden="true" />}
            </span>
            {/* "What's this?" first-visit hint — small floating label
                with a subtle tail pointing up at the beacon. Auto-fades. */}
            {hintVisible && !isOpen && (
                <span
                    className="jns-tour-beacon-hint"
                    role="tooltip"
                    aria-hidden="true"
                    onClick={handleClick}
                    data-testid={`tour-beacon-hint-${id}`}
                >
                    <span className="jns-tour-beacon-hint-dot" />
                    {hintText}
                </span>
            )}
            <style>{`
                .jns-tour-beacon {
                    background: linear-gradient(135deg, #FFF1DC 0%, #F4C18B 58%, #DFA16D 100%);
                    color: #5A2B20;
                    border: 1.5px solid rgba(177, 112, 70, 0.62);
                    box-shadow:
                        0 4px 10px -5px rgba(136, 73, 42, 0.45),
                        0 0 0 2px rgba(255, 248, 238, 0.82);
                    font-weight: 700;
                }
                .jns-tour-beacon-size-sm { width: 18px; height: 18px; font-size: 11px; }
                .jns-tour-beacon-size-md { width: 24px; height: 24px; font-size: 14px; }
                .jns-tour-beacon-size-lg { width: 30px; height: 30px; font-size: 17px; }
                @media (min-width: 768px) {
                    .jns-tour-beacon-size-sm { width: 19px; height: 19px; font-size: 11.5px; }
                    .jns-tour-beacon-size-md { width: 26px; height: 26px; font-size: 15px; }
                    .jns-tour-beacon-size-lg { width: 32px; height: 32px; font-size: 19px; }
                }
                @media (min-width: 1024px) {
                    .jns-tour-beacon-size-sm { width: 20px; height: 20px; font-size: 12px; }
                    .jns-tour-beacon-size-md { width: 28px; height: 28px; font-size: 16px; }
                    .jns-tour-beacon-size-lg { width: 34px; height: 34px; font-size: 20px; }
                }
                .jns-tour-beacon-glyph {
                    font-family: ui-serif, Georgia, "DM Serif Display", serif;
                    line-height: 1;
                    transform: translateY(-1px);
                }
                /* Continuous outer halo — visible even when not pulsing. */
                .jns-tour-beacon-halo {
                    position: absolute;
                    inset: -5px;
                    border-radius: 9999px;
                    background: radial-gradient(circle, rgba(232, 160, 99, 0.24) 0%, rgba(232, 160, 99, 0) 70%);
                    z-index: -1;
                    pointer-events: none;
                    animation: jnsTourHaloPulse 5s ease-in-out infinite;
                }
                .jns-tour-beacon-idle {
                    animation: jnsTourPulseIdle 5s ease-in-out infinite;
                }
                .jns-tour-beacon-unseen {
                    animation: jnsTourPulseUnseen 5s ease-in-out infinite;
                }
                .jns-tour-beacon-active {
                    background: linear-gradient(135deg, #6B2C2A 0%, #B9663E 55%, #E8A063 100%);
                    color: #FFF8EE;
                    border-color: rgba(60, 20, 20, 0.95);
                    box-shadow:
                        0 8px 18px -4px rgba(80, 30, 20, 0.7),
                        0 0 0 3px rgba(255, 248, 238, 0.95),
                        0 0 0 8px rgba(232, 160, 99, 0.3);
                    transform: scale(1.12);
                }
                .jns-tour-beacon:hover {
                    transform: translateY(-1px) scale(1.06);
                    box-shadow:
                        0 7px 16px -8px rgba(136, 73, 42, 0.55),
                        0 0 0 2px rgba(255, 248, 238, 0.94);
                }
                .jns-tour-beacon:focus-visible {
                    outline: 3px solid #B9663E;
                    outline-offset: 3px;
                }
                @keyframes jnsTourPulseIdle {
                    0%, 100% { transform: scale(1); }
                    50%      { transform: scale(1.025); }
                }
                @keyframes jnsTourPulseUnseen {
                    0%   { transform: scale(1); }
                    35%  { transform: scale(1.055); }
                    70%  { transform: scale(1.018); }
                    100% { transform: scale(1); }
                }
                @keyframes jnsTourHaloPulse {
                    0%, 100% { opacity: 0.28; transform: scale(1); }
                    50%      { opacity: 0.55; transform: scale(1.14); }
                }
                /* "What's this?" floating hint label — small, soft,
                    fades in once on the customer's first land. */
                .jns-tour-beacon-hint {
                    position: absolute;
                    top: 100%;
                    right: 0;
                    margin-top: 8px;
                    display: inline-flex; align-items: center; gap: 6px;
                    padding: 5px 10px 5px 8px;
                    border-radius: 999px;
                    background: linear-gradient(135deg, #6B2C2A 0%, #B9663E 100%);
                    color: #FFF8EE;
                    font-size: 11px; font-weight: 700;
                    white-space: nowrap;
                    box-shadow:
                        0 10px 22px -10px rgba(80, 30, 30, 0.5),
                        0 0 0 1px rgba(255, 248, 238, 0.6);
                    pointer-events: auto;
                    cursor: pointer;
                    z-index: 31;
                    opacity: 0;
                    transform: translateY(-4px) scale(0.96);
                    animation: jnsTourHintIn 360ms cubic-bezier(0.16, 1, 0.3, 1) forwards,
                               jnsTourHintOut 360ms cubic-bezier(0.4, 0, 1, 0.8) forwards;
                    animation-delay: 0ms, ${HINT_FADE_AFTER_MS - 360}ms;
                }
                .jns-tour-beacon-hint-dot {
                    width: 5px; height: 5px;
                    border-radius: 999px;
                    background: #FFD79E;
                    box-shadow: 0 0 0 2px rgba(255, 248, 238, 0.7);
                }
                @keyframes jnsTourHintIn {
                    0%   { opacity: 0; transform: translateY(-4px) scale(0.96); }
                    100% { opacity: 1; transform: translateY(0) scale(1); }
                }
                @keyframes jnsTourHintOut {
                    0%   { opacity: 1; transform: translateY(0) scale(1); }
                    100% { opacity: 0; transform: translateY(-6px) scale(0.96); }
                }
                @media (prefers-reduced-motion: reduce) {
                    .jns-tour-beacon-idle,
                    .jns-tour-beacon-unseen,
                    .jns-tour-beacon-halo,
                    .jns-tour-beacon-hint {
                        animation: none !important;
                    }
                    .jns-tour-beacon-hint { opacity: 1; transform: none; }
                }
            `}</style>
        </span>
    );
}
